import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import {
  formatConceptDate,
  getConcept,
  sectionId,
} from "@/lib/concepts";

export const Route = createFileRoute("/concepts/$slug")({
  loader: ({ params }) => {
    const post = getConcept(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Not found — Near & Far | Double Lion" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const url = `https://doublelion.com/concepts/${post.slug}`;
    return {
      meta: [
        { title: `${post.title} — Near & Far | Double Lion` },
        { name: "description", content: post.summary },
        ...(post.draft
          ? [{ name: "robots", content: "noindex, nofollow" }]
          : []),
        { property: "og:title", content: `${post.title} — Near & Far` },
        { property: "og:description", content: post.summary },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.summary,
            datePublished: post.written,
            dateModified: post.updated,
            mainEntityOfPage: url,
            author: { "@type": "Organization", name: "Double Lion" },
            publisher: { "@type": "Organization", name: "Double Lion" },
          }),
        },
      ],
    };
  },
  notFoundComponent: ConceptNotFound,
  component: ConceptNote,
});

function ConceptNotFound() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-40 px-6 pb-32 mx-auto max-w-3xl">
        <h1 className="font-mono text-2xl uppercase tracking-[0.06em]">
          Note not found
        </h1>
        <Link
          to="/concepts"
          className="mt-8 inline-flex font-mono text-[11px] uppercase tracking-[0.25em] border-b border-foreground pb-1"
        >
          ← All concepts
        </Link>
      </main>
    </div>
  );
}

function ConceptNote() {
  const { post } = Route.useLoaderData();

  const metaLabel = "font-mono text-[9px] uppercase tracking-[0.25em] text-muted-foreground";
  const metaValue = "font-mono text-[11px] uppercase tracking-[0.12em]";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-24 pb-32">
        <div className="px-6 pt-10">
          <div className="mx-auto max-w-4xl">
            {/* Reading pane */}
            <article className="min-w-0" id="top">
              {/* Breadcrumb */}
              <nav
                aria-label="Breadcrumb"
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground flex items-center flex-wrap gap-y-2"
              >
                <Link to="/concepts" className="hover:text-foreground transition-colors">
                  Near &amp; Far
                </Link>
                <span className="mx-2">/</span>
                <span className="text-foreground">{post.title}</span>
                {post.draft && (
                  <span className="ml-3 px-2 py-1 bg-ink text-ink-foreground tracking-[0.2em]">
                    Draft
                  </span>
                )}
              </nav>

              {/* Cover */}
              <div className="mt-8 h-48 md:h-64 w-full flex items-center justify-center overflow-hidden border border-border">
                <img
                  src={post.image}
                  alt={`Cover illustration for ${post.title}`}
                  loading="lazy"
                  width={768}
                  height={768}
                  className="h-full w-auto object-contain mix-blend-multiply"
                />
              </div>

              <h1 className="mt-10 font-mono text-3xl md:text-4xl uppercase tracking-[0.04em] leading-tight">
                {post.title}
              </h1>

              {/* Metadata strip */}
              <dl className="mt-8 grid gap-4 sm:grid-cols-2 border-y border-border py-4">
                <div>
                  <dt className={metaLabel}>Written</dt>
                  <dd className={metaValue}>{formatConceptDate(post.written)}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className={metaLabel}>Intended audience</dt>
                  <dd className={metaValue}>{post.audience.join(", ")}</dd>
                </div>
              </dl>

              <p className="mt-10 text-lg md:text-xl leading-relaxed">
                {post.intro}
              </p>

              {post.sections.map((s) => (
                <section key={s.heading} className="mt-14 scroll-mt-28" id={sectionId(s.heading)}>
                  <h2 className="font-mono text-base uppercase tracking-[0.18em]">
                    {s.heading}
                  </h2>
                  <div className="mt-5 grid gap-10 lg:grid-cols-[minmax(0,1fr)_260px] items-start">
                    <div className="space-y-5 text-[17px] leading-[1.8] text-foreground/85">
                      {s.paragraphs.map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                    </div>
                    {s.quote && (
                      <blockquote className="border-l-2 border-ink pl-4 font-serif italic text-lg leading-snug text-foreground/80">
                        “{s.quote.text}”
                        {s.quote.attribution && (
                          <footer className="mt-2 font-mono not-italic text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                            {s.quote.attribution}
                          </footer>
                        )}
                      </blockquote>
                    )}
                  </div>
                  {s.figure && (
                    <figure className="mt-8">
                      <img
                        src={s.figure.src}
                        alt={s.figure.alt}
                        loading="lazy"
                        width={1024}
                        height={768}
                        className={`w-full h-auto border border-border ${s.figure.photo ? "object-cover" : "mix-blend-multiply"}`}
                      />
                      {s.figure.caption && (
                        <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {s.figure.caption}
                        </figcaption>
                      )}
                    </figure>
                  )}
                  {s.figures && s.figures.length > 0 && (
                    <figure className="mt-8">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {s.figures.map((fig, idx) => (
                          <div key={idx}>
                            <img
                              src={fig.src}
                              alt={fig.alt}
                              loading="lazy"
                              width={1024}
                              height={768}
                              className={`w-full h-auto border border-border ${fig.photo ? "object-cover" : "mix-blend-multiply"}`}
                            />
                            {fig.caption && (
                              <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                {fig.caption}
                              </figcaption>
                            )}
                          </div>
                        ))}
                      </div>
                    </figure>
                  )}

                </section>
              ))}

            </article>
          </div>
        </div>
      </main>
    </div>
  );
}
